import * as React from "react";
import { cn } from "@/lib/utils";

export interface FilterChipStripValue {
  status: "all" | "RUNNING" | "STOPPED" | "PARTIAL" | "IDLE";
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents -- "all" eh sentinela de UI distinguivel de id real
  responsible: string | "all";
  oee: "all" | "high" | "mid" | "low";
}

export interface FilterChipStripProps {
  value: FilterChipStripValue;
  onChange: (next: FilterChipStripValue) => void;
  responsibles: string[];
  counters: { lines: number; machines: number };
}

const STATUS_OPTS: { value: FilterChipStripValue["status"]; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "RUNNING", label: "Em produção" },
  { value: "PARTIAL", label: "Parcial" },
  { value: "STOPPED", label: "Parada" },
  { value: "IDLE", label: "Ociosa" },
];

const OEE_OPTS: { value: FilterChipStripValue["oee"]; label: string }[] = [
  { value: "all", label: "Qualquer OEE" },
  { value: "high", label: "OEE ≥85" },
  { value: "mid", label: "OEE 65–84" },
  { value: "low", label: "OEE <65" },
];

function ChipGroup<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (next: T) => void;
}) {
  return (
    <fieldset className="flex flex-wrap items-center gap-1.5" aria-label={label}>
      <legend className="sr-only">{label}</legend>
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            className={cn(
              "inline-flex h-7 items-center rounded-full border px-3 text-[12px] font-medium",
              "transition-colors",
              "focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]",
              active
                ? "border-brand-500/40 bg-brand-50 text-brand-700"
                : "border-border-default bg-bg-surface text-text-secondary hover:bg-bg-muted-rd"
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </fieldset>
  );
}

export function FilterChipStrip({
  value,
  onChange,
  responsibles,
  counters,
}: FilterChipStripProps): React.ReactElement {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex flex-wrap items-center gap-4">
        <ChipGroup
          label="Filtrar por status"
          options={STATUS_OPTS}
          value={value.status}
          onChange={(v) => onChange({ ...value, status: v })}
        />

        <div className="h-5 w-px bg-border-subtle" aria-hidden="true" />

        <label className="flex items-center gap-2 text-[12px] text-text-secondary">
          <span className="sr-only">Responsável</span>
          <select
            value={value.responsible}
            onChange={(e) => onChange({ ...value, responsible: e.target.value })}
            className={cn(
              "h-7 rounded-md border border-border-default bg-bg-surface",
              "px-2 text-[12px] text-text-primary",
              "focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]"
            )}
          >
            <option value="all">Todos responsáveis</option>
            {responsibles.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>

        <div className="h-5 w-px bg-border-subtle" aria-hidden="true" />

        <ChipGroup
          label="Filtrar por OEE"
          options={OEE_OPTS}
          value={value.oee}
          onChange={(v) => onChange({ ...value, oee: v })}
        />
      </div>

      <span className="font-mono text-[12px] text-text-tertiary">
        {counters.lines} linha{counters.lines === 1 ? "" : "s"} · {counters.machines} máquina
        {counters.machines === 1 ? "" : "s"}
      </span>
    </div>
  );
}
